import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Calendar, Plus, Pencil, Trash2, Send, Lock, Info,
  CheckCircle2, AlertCircle, FileText, LayoutGrid, List,
  ArrowUpDown, ArrowUp, ArrowDown, Clock, Coffee, MapPin,
} from 'lucide-react';
import { api, DAY_LABELS } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';
import { confirmDialog } from '@/components/ui/confirm-dialog';

const ALL_DAYS = ['senin', 'selasa', 'rabu', 'kamis', 'jumat', 'sabtu'];
const EMPTY = { day: 'senin', start_time: '07:00', end_time: '08:00', class_id: '', subject_id: '', room_id: '', semester: 'ganjil', academic_year_id: '' };

function StatusBadge({ status }) {
  if (status === 'locked') return <Badge className="bg-rose-100 text-rose-700 border-rose-200 gap-1"><Lock className="h-3 w-3" /> Terkunci</Badge>;
  if (status === 'approved') return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1"><CheckCircle2 className="h-3 w-3" /> Disetujui</Badge>;
  if (status === 'submitted') return <Badge className="bg-blue-100 text-blue-700 border-blue-200 gap-1"><Send className="h-3 w-3" /> Terkirim</Badge>;
  return <Badge variant="outline" className="text-amber-600 border-amber-300 gap-1"><FileText className="h-3 w-3" /> Draft</Badge>;
}

export default function MySchedulePage() {
  const { user, activeRole } = useAuth();
  const [items, setItems] = useState([]);
  const [grid, setGrid] = useState({ days: [], slots: {}, grid: {} });
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [gridMode, setGridMode] = useState('view'); // 'view' (own schedule) | 'input' (by class for conflict checking)
  const [selectedClassId, setSelectedClassId] = useState('');
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [activeAY, setActiveAY] = useState(null);
  const [teachingSlots, setTeachingSlots] = useState([]);
  const [allTeachingSlots, setAllTeachingSlots] = useState(null); // Store full settings
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [selectedSlots, setSelectedSlots] = useState([]); // v1.1.1: Multi-slot selector

  // Sorting state for list view
  const [sortColumn, setSortColumn] = useState('day'); // day, jam, jtm, class, subject, room
  const [sortDirection, setSortDirection] = useState('asc'); // asc, desc

  // Check if user is siswa or currently viewing as wali_kelas
  const isSiswa = user?.roles?.includes('siswa');
  const isViewingAsWaliKelas = activeRole === 'wali_kelas';
  const homeroomClassId = user?.homeroom_class_id;

  const loadGridData = async () => {
    // In view mode:
    //   - If activeRole is 'wali_kelas': show homeroom class schedule
    //   - Otherwise (activeRole is 'guru'): show teacher's own schedule
    // In input mode: show selected class schedule with all teachers
    const params = {};
    if (gridMode === 'view') {
      if (isViewingAsWaliKelas && homeroomClassId) {
        // Viewing as wali_kelas: show homeroom class schedule
        params.class_id = homeroomClassId;
      } else {
        // Viewing as guru: show teacher's own schedule
        params.teacher_id = user?.id;
      }
    } else if (gridMode === 'input' && selectedClassId) {
      params.class_id = selectedClassId;
    }
    const g = await api.get('/schedules/grid', { params });

    // Process grid data to ensure status field in grid items
    const gridData = g.data || { days: [], slots: {}, grid: {} };

    // Ensure days is array, slots can be array (legacy) or object (per-day)
    if (!Array.isArray(gridData.days)) gridData.days = [];
    if (!gridData.slots) gridData.slots = {};
    if (!gridData.grid) gridData.grid = {};

    if (gridData.grid) {
      Object.keys(gridData.grid).forEach(day => {
        Object.keys(gridData.grid[day]).forEach(time => {
          if (gridData.grid[day][time]) {
            gridData.grid[day][time].status = gridData.grid[day][time].status || 'draft';
          }
        });
      });
    }

    setGrid(gridData);
  };

  const refresh = async () => {
    try {
      const ay = await api.get('/academic-years/active');
      const activeAYData = ay.data;

      // Load schedule based on current active role
      // - wali_kelas: load homeroom class schedule
      // - guru: load teacher's own schedule
      const scheduleParams = {};
      if (isViewingAsWaliKelas && homeroomClassId) {
        scheduleParams.class_id = homeroomClassId;
      } else {
        scheduleParams.teacher_id = user?.id;
      }

      const [s, c, sub, r, settings] = await Promise.all([
        // Use grouped endpoint for JTM grouping
        api.get('/schedules/grouped', { params: scheduleParams }),
        // Load classes for active academic year
        activeAYData ? api.get('/classes', { params: { academic_year_id: activeAYData.id } }) : api.get('/classes'),
        api.get('/subjects'),
        api.get('/rooms'),
        api.get('/settings'),
      ]);
      // Ensure status field exists with default 'draft'
      const itemsWithStatus = (s.data || []).map(item => ({
        ...item,
        status: item.status || 'draft'
      }));
      setItems(itemsWithStatus);

      // Filter classes based on active role
      // - wali_kelas: only show homeroom class
      // - guru: show all classes
      const allClasses = c.data || [];

      if (isViewingAsWaliKelas && homeroomClassId) {
        const filteredClasses = allClasses.filter(cls => cls.id === homeroomClassId);
        setClasses(filteredClasses);
        // Auto-select homeroom class for wali kelas in input mode
        if (!selectedClassId) {
          setSelectedClassId(homeroomClassId);
        }
      } else {
        setClasses(allClasses);
      }

      setSubjects(sub.data || []);
      setRooms(r.data || []);
      setActiveAY(activeAYData);

      // Store full teaching slots settings
      const slotsData = settings.data?.teaching_slots || [];
      setAllTeachingSlots(slotsData);

      // Get initial teaching slots (use first day or global)
      let allSlots = [];
      if (Array.isArray(slotsData)) {
        // Legacy: global slots
        allSlots = slotsData;
      } else if (typeof slotsData === 'object') {
        // New: per-day slots - use senin as default
        allSlots = slotsData['senin'] || Object.values(slotsData)[0] || [];
      }
      // Include breaks in teachingSlots with originalIndex
      const slotsWithIndex = allSlots.map((slot, originalIndex) => ({ ...slot, originalIndex }));
      setTeachingSlots(slotsWithIndex);

      // Load initial grid data
      await loadGridData();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [user?.id]);
  useEffect(() => { if (viewMode === 'grid') loadGridData(); }, [gridMode, selectedClassId]);

  // Update teaching slots when selected day changes
  // v1.1.1 FIX: Keep original index to correctly map to backend slot_indexes
  useEffect(() => {
    if (!allTeachingSlots) return;

    let daySlots = [];
    if (Array.isArray(allTeachingSlots)) {
      // Global slots - same for all days
      daySlots = allTeachingSlots;
    } else if (typeof allTeachingSlots === 'object') {
      // Per-day slots - get slots for selected day
      daySlots = allTeachingSlots[form.day] || [];
    }

    // v1.1.1 UPDATE: Include BREAKS in the UI for better visibility
    // Store original index from full slots array (including breaks)
    const slotsWithOriginalIndex = daySlots
      .map((slot, originalIndex) => ({ ...slot, originalIndex }));

    setTeachingSlots(slotsWithOriginalIndex);
  }, [form.day, allTeachingSlots]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, academic_year_id: activeAY?.id, semester: activeAY?.active_semester || (new Date().getMonth() >= 6 ? 'ganjil' : 'genap'), teacher_id: user?.id });
    setSelectedSlots([]); // v1.1.1: Reset multi-slot selection
    setOpen(true);
  };
  const openEdit = (s) => {
    if (s.status === 'locked') { toast.error('Jadwal terkunci tidak bisa diedit'); return; }
    if (s.status === 'submitted') { toast.error('Jadwal sudah dikirim ke admin, tidak bisa diedit lagi'); return; }
    setEditing(s);
    setForm({ ...s });
    // v1.1.1: Load existing slot_indexes if available
    setSelectedSlots(s.slot_indexes || []);
    setOpen(true);
  };

  // v1.1.1: Multi-slot toggle handler
  const handleSlotToggle = (slotIndex) => {
    const slot = teachingSlots[slotIndex];
    if (!slot) return;

    const originalIndex = slot.originalIndex; // Get the original index from full array

    if (selectedSlots.includes(originalIndex)) {
      setSelectedSlots(selectedSlots.filter(i => i !== originalIndex));
    } else {
      setSelectedSlots([...selectedSlots, originalIndex].sort((a, b) => a - b));
    }
  };
  const handleSubmit = async () => {
    if (!form.class_id || !form.subject_id) {
      toast.error('Kelas dan Mapel wajib dipilih');
      return;
    }

    // v1.1.1: Build payload with slot_indexes if multi-slot is used
    const payload = { ...form, teacher_id: user.id };

    if (selectedSlots.length > 0) {
      // Multi-slot mode: use slot_indexes
      payload.slot_indexes = selectedSlots.sort((a, b) => a - b);

      // Calculate start_time and end_time from selected slots
      const selectedTeachingSlots = teachingSlots.filter(s => selectedSlots.includes(s.originalIndex));
      selectedTeachingSlots.sort((a, b) => a.originalIndex - b.originalIndex);

      const firstSlot = selectedTeachingSlots[0];
      const lastSlot = selectedTeachingSlots[selectedTeachingSlots.length - 1];

      if (firstSlot && lastSlot) {
        payload.start_time = firstSlot.start_time;
        payload.end_time = lastSlot.end_time;
      }
    } else {
      // No slot selected
      toast.error('Pilih minimal 1 jam mengajar');
      return;
    }

    // Remove room_id - will be auto-assigned by backend based on class
    delete payload.room_id;

    try {
      if (editing) await api.put(`/schedules/${editing.id}`, payload);
      else await api.post('/schedules', payload);
      toast.success('Jadwal disimpan sebagai draft');
      setOpen(false); await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === 'object') {
        const msg = detail.message || 'Jadwal bentrok';
        toast.error(msg, { duration: 8000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Gagal');
      }
    }
  };
  const handleDelete = async (s) => {
    if (s.status === 'locked') { toast.error('Jadwal terkunci tidak bisa dihapus'); return; }
    if (!(await confirmDialog('Hapus jadwal ini?'))) return;
    try { await api.delete(`/schedules/${s.id}`); toast.success('Dihapus'); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Gagal'); }
  };
  const handleSubmitDraft = async (s) => {
    if (!(await confirmDialog(`Kirim jadwal "${s.subject_name} - ${s.class_name}" ke Admin? Setelah dikirim Anda tidak bisa edit lagi.`))) return;
    try { await api.put(`/schedules/${s.id}/submit`); toast.success('Jadwal terkirim ke Admin'); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Gagal'); }
  };
  const handleSubmitAllDrafts = async () => {
    const drafts = items.filter((s) => s.status === 'draft');
    if (drafts.length === 0) { toast.error('Tidak ada draft untuk dikirim'); return; }
    if (!(await confirmDialog(`Kirim ${drafts.length} jadwal draft ke Admin sekaligus?`))) return;
    try {
      for (const d of drafts) {
        await api.put(`/schedules/${d.id}/submit`);
      }
      toast.success(`${drafts.length} jadwal terkirim ke Admin`);
      await refresh();
    } catch (e) { toast.error('Sebagian gagal: ' + (e?.response?.data?.detail || '')); }
  };

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort items based on current sort column and direction
  const sortedItems = [...items].sort((a, b) => {
    let aVal, bVal;

    switch (sortColumn) {
      case 'day':
        // Sort by day order
        const dayOrder = ['senin', 'selasa', 'rabu', 'kamis', 'jumat', 'sabtu', 'minggu'];
        aVal = dayOrder.indexOf(a.day?.toLowerCase() || '');
        bVal = dayOrder.indexOf(b.day?.toLowerCase() || '');
        break;
      case 'jam':
        // Sort by start_time
        aVal = a.start_time || '';
        bVal = b.start_time || '';
        break;
      case 'jtm':
        // Sort by jtm_count
        aVal = a.jtm_count || 1;
        bVal = b.jtm_count || 1;
        break;
      case 'class':
        // Sort by class_name
        aVal = (a.class_name || '').toLowerCase();
        bVal = (b.class_name || '').toLowerCase();
        break;
      case 'subject':
        // Sort by subject_name
        aVal = (a.subject_name || '').toLowerCase();
        bVal = (b.subject_name || '').toLowerCase();
        break;
      case 'room':
        // Sort by room_name
        aVal = (a.room_name || '').toLowerCase();
        bVal = (b.room_name || '').toLowerCase();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) return <div className="text-sm text-slate-500">Memuat...</div>;

  const drafts = items.filter((s) => s.status === 'draft');
  const submitted = items.filter((s) => s.status === 'submitted');
  const approved = items.filter((s) => s.status === 'approved');
  const locked = items.filter((s) => s.status === 'locked');

  return (
    <div className="space-y-6" data-testid="my-schedule-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Badge className="bg-[#006837]/10 text-[#006837] border-[#006837]/20 mb-2">
            <Calendar className="h-3 w-3 mr-1" /> Jadwal Mengajar Saya
          </Badge>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Atur Jadwal Mengajar</h1>
          <p className="text-sm text-slate-600 mt-1">Atur jadwal Anda sendiri (draft), kirim ke Admin, lalu Admin akan mengunci jadwal final</p>
        </div>
        <div className="flex gap-2">
          {drafts.length > 0 && viewMode === 'list' && (
            <Button onClick={handleSubmitAllDrafts} variant="outline" className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="submit-all-button">
              <Send className="h-4 w-4" /> Kirim Semua Draft ({drafts.length})
            </Button>
          )}
          {viewMode === 'list' && (
            <Button onClick={openCreate} className="bg-[#006837] hover:bg-[#0B7A3B] gap-2" data-testid="add-schedule-button">
              <Plus className="h-4 w-4" /> Tambah Jadwal
            </Button>
          )}
        </div>
      </div>

      <Alert className="border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-700" />
        <AlertDescription className="text-blue-900 text-sm">
          <strong>Alur Jadwal:</strong> 1️⃣ <strong>Draft</strong> (Anda bisa edit) → 2️⃣ Klik <strong>Kirim</strong> ke Admin → 3️⃣ <strong>Terkirim</strong> (menunggu persetujuan) → 4️⃣ Admin <strong>Menyetujui</strong> → 5️⃣ Admin <strong>Mengunci</strong> jadwal final (status: <strong>Terkunci</strong>)
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <StatCard icon={FileText} label="Draft" value={drafts.length} color="bg-amber-50 border-amber-200 text-amber-700" />
        <StatCard icon={Send} label="Terkirim" value={submitted.length} color="bg-blue-50 border-blue-200 text-blue-700" />
        <StatCard icon={CheckCircle2} label="Disetujui" value={approved.length} color="bg-emerald-50 border-emerald-200 text-emerald-700" />
        <StatCard icon={Lock} label="Terkunci" value={locked.length} color="bg-rose-50 border-rose-200 text-rose-700" />
      </div>

      {/* View Mode Toggle */}
      <div className="flex justify-end gap-2">
        <Button variant={viewMode === 'grid' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('grid')} className={viewMode === 'grid' ? 'bg-[#006837]' : ''}>
          <LayoutGrid className="h-4 w-4 mr-1" /> Grid
        </Button>
        <Button variant={viewMode === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('list')} className={viewMode === 'list' ? 'bg-[#006837]' : ''}>
          <List className="h-4 w-4 mr-1" /> List
        </Button>
      </div>

      {viewMode === 'grid' ? (
        <>
          {/* Info banner for wali kelas */}
          {isViewingAsWaliKelas && homeroomClassId && gridMode === 'input' && (
            <Alert className="border-[#006837] bg-emerald-50">
              <Info className="h-4 w-4 text-emerald-700" />
              <AlertDescription className="text-emerald-900 text-sm">
                Sebagai Wali Kelas, Anda hanya dapat melihat dan mengatur jadwal untuk kelas yang Anda pegang: <strong>{classes.find(c => c.id === homeroomClassId)?.name || 'Kelas Anda'}</strong>
              </AlertDescription>
            </Alert>
          )}

          {/* Grid Mode Controls */}
          <Card>
            <CardContent className="p-4 flex flex-wrap items-center gap-3">
              <div className="flex gap-2">
                <Button
                  variant={gridMode === 'view' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setGridMode('view')}
                  className={gridMode === 'view' ? 'bg-[#006837]' : ''}
                >
                  {isViewingAsWaliKelas ? 'Lihat Jadwal Kelas' : 'Lihat Jadwal Saya'}
                </Button>
                <Button
                  variant={gridMode === 'input' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setGridMode('input')}
                  className={gridMode === 'input' ? 'bg-[#006837]' : ''}
                >
                  {isViewingAsWaliKelas ? 'Input Jadwal Kelas' : 'Input Jadwal (Per Kelas)'}
                </Button>
              </div>
              {gridMode === 'input' && (
                <div className="flex-1 min-w-[200px]">
                  <Select
                    value={selectedClassId}
                    onValueChange={setSelectedClassId}
                    disabled={isViewingAsWaliKelas && homeroomClassId} // Disable for wali kelas
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pilih kelas untuk input jadwal..." />
                    </SelectTrigger>
                    <SelectContent>
                      {classes.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
          {gridMode === 'input' && !selectedClassId && (
            <Alert className="border-amber-200 bg-amber-50">
              <Info className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-amber-900 text-sm">
                Pilih kelas terlebih dahulu untuk melihat jadwal kelas dan menghindari bentrok dengan guru lain.
              </AlertDescription>
            </Alert>
          )}
          <Card>
          <CardContent className="p-3">
            {/* Color Legend */}
            <div className="flex items-center gap-3 flex-wrap mb-2 px-1 text-[11px] text-slate-600">
              <span className="font-semibold">Petunjuk Status:</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-amber-100 border border-amber-300" /> Draft
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-blue-100 border border-blue-300" /> Terkirim
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-emerald-100 border border-emerald-300" /> Disetujui
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded bg-sky-200 border border-sky-400" /> Terkunci
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border-2 border-[#006837] shadow-lg">
              <table className="w-full border-collapse text-xs bg-white" data-testid="schedule-grid-table">
                <thead>
                  <tr className="bg-gradient-to-r from-[#006837] to-[#008547]">
                    {(grid.days || []).map((d) => (
                      <React.Fragment key={d}>
                        <th className="bg-[#004d28] text-white border-r border-[#006837]/30 p-3 text-center font-bold min-w-[120px]">
                          <div className="flex items-center justify-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span className="text-xs">Waktu</span>
                          </div>
                        </th>
                        <th className="text-white border-r-2 border-white/20 p-3 text-center font-bold min-w-[200px]">
                          <div className="flex items-center justify-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <span className="text-sm capitalize">{DAY_LABELS[d]}</span>
                          </div>
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Per-day rendering with slot column for each day */}
                  {(() => {
                    const slotsData = grid.slots || {};

                    // Find maximum number of slots across all days
                    let maxSlots = 0;
                    if (Array.isArray(slotsData)) {
                      maxSlots = slotsData.length;
                    } else {
                      Object.values(slotsData).forEach(daySlots => {
                        maxSlots = Math.max(maxSlots, daySlots.length);
                      });
                    }

                    const rows = [];
                    for (let slotIdx = 0; slotIdx < maxSlots; slotIdx++) {
                      rows.push(
                        <tr key={`slot-${slotIdx}`} className="border-b border-slate-200 hover:bg-slate-50/50 transition-colors">
                          {(grid.days || []).map((day) => {
                            const daySlotsData = Array.isArray(slotsData) ? slotsData : (slotsData[day] || []);
                            const slot = daySlotsData[slotIdx];

                            if (!slot) {
                              return (
                                <React.Fragment key={day}>
                                  <td className="border-r border-slate-200 p-2 bg-slate-100/50"></td>
                                  <td className="border-r-2 border-slate-300 p-2 bg-slate-50/30"></td>
                                </React.Fragment>
                              );
                            }

                            const s = grid.grid?.[day]?.[slot.start_time];

                            // Time column for this day
                            const timeCell = (
                              <td className={`border-r border-slate-200 p-3 text-center ${slot.is_break ? 'bg-amber-100/80' : 'bg-slate-100'}`}>
                                <div className="flex flex-col items-center gap-1">
                                  {slot.is_break && <Coffee className="h-3.5 w-3.5 text-amber-700" />}
                                  <div className="font-bold text-slate-900 text-[11px]">{slot.name}</div>
                                  <div className="font-mono text-[10px] text-slate-600">{slot.start_time}</div>
                                  <div className="font-mono text-[10px] text-slate-600">{slot.end_time}</div>
                                </div>
                              </td>
                            );

                            // Schedule cell
                            let scheduleCell;
                            if (slot.is_break) {
                              scheduleCell = (
                                <td className="border-r-2 border-slate-300 p-3 bg-amber-50/90 text-center">
                                  <div className="flex items-center justify-center gap-2 text-amber-800 font-semibold">
                                    <Coffee className="h-4 w-4" />
                                    <span className="text-xs italic">Istirahat</span>
                                  </div>
                                </td>
                              );
                            } else if (s) {
                              const statusColors = {
                                draft: 'bg-amber-50 hover:bg-amber-100 border-amber-300 text-amber-900',
                                submitted: 'bg-blue-50 hover:bg-blue-100 border-blue-300 text-blue-900',
                                approved: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-300 text-emerald-900',
                                locked: 'bg-sky-100 hover:bg-sky-200 border-sky-400 text-sky-900',
                              };
                              const sStatus = s?.status || 'draft';
                              const cellClass = statusColors[sStatus] || statusColors.draft;

                              // Only allow edit if it's user's own schedule and still draft
                              const canEdit = s.teacher_id === user?.id && s.status === 'draft';

                              scheduleCell = (
                                <td className="border-r-2 border-slate-300 p-2 align-top bg-white">
                                  <button
                                    type="button"
                                    onClick={() => canEdit && openEdit(s)}
                                    disabled={!canEdit}
                                    className={`w-full text-left p-3 rounded-lg border-2 ${cellClass} transition-all duration-200 ${!canEdit ? 'cursor-not-allowed opacity-80' : 'hover:shadow-lg hover:-translate-y-0.5'}`}
                                    data-testid={`grid-cell-${day}-${slot.start_time}`}
                                    title={`${s.subject_name || s.subject_code} • ${s.teacher_name || ''} • ${sStatus}`}
                                  >
                                    <div className="font-bold truncate flex items-center gap-1.5 mb-1.5">
                                      <span className="text-sm">{s.subject_code || s.subject_name?.slice(0, 12)}</span>
                                      {sStatus === 'locked' && <Lock className="h-3.5 w-3.5 inline-block flex-shrink-0" />}
                                      {sStatus === 'approved' && <CheckCircle2 className="h-3.5 w-3.5 inline-block flex-shrink-0" />}
                                      {sStatus === 'submitted' && <Send className="h-3.5 w-3.5 inline-block flex-shrink-0" />}
                                      {sStatus === 'draft' && <span className="text-[10px] opacity-70 font-normal">(Draft)</span>}
                                    </div>
                                    {gridMode === 'input' && (
                                      <div className="text-[11px] truncate opacity-90 font-semibold mb-1">{s.teacher_name}</div>
                                    )}
                                    <div className="text-[11px] truncate opacity-90 mb-1.5 font-medium">{s.class_name}</div>
                                    <div className="text-[10px] font-mono opacity-75 flex items-center gap-1">
                                      <MapPin className="h-3 w-3" />
                                      <span>{s.room_name}</span>
                                    </div>
                                  </button>
                                </td>
                              );
                            } else {
                              // Empty cell
                              scheduleCell = (
                                <td className="border-r-2 border-slate-300 p-2 align-top bg-white">
                                  {gridMode === 'input' && selectedClassId ? (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setForm({
                                          ...EMPTY,
                                          day,
                                          start_time: slot.start_time,
                                          end_time: slot.end_time,
                                          class_id: selectedClassId,
                                          academic_year_id: activeAY?.id,
                                          semester: activeAY?.active_semester || (new Date().getMonth() >= 6 ? 'ganjil' : 'genap'),
                                          teacher_id: user?.id
                                        });
                                        setOpen(true);
                                      }}
                                      className="w-full h-12 rounded border border-dashed border-slate-300 hover:border-[#006837] hover:bg-[#006837]/5 transition-colors text-slate-300 hover:text-[#006837] text-xs"
                                      data-testid={`grid-empty-${day}-${slot.start_time}`}
                                    >
                                      +
                                    </button>
                                  ) : null}
                                </td>
                              );
                            }

                            return (
                              <React.Fragment key={day}>
                                {timeCell}
                                {scheduleCell}
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    }
                    return rows;
                  })()}
                </tbody>
              </table>
              {(grid.days || []).length === 0 && (
                <div className="text-center py-8 text-slate-500">Loading grid...</div>
              )}
            </div>
          </CardContent>
        </Card>
        </>
      ) : (
        <Card>
        <CardContent className="p-3">
          <div className="mb-3 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
            <span className="font-semibold">JTM (Jam Tugas Mengajar):</span> Jam mengajar berdekatan di kelas, hari, dan mata pelajaran yang sama digabung menjadi 1 entry.
          </div>
        </CardContent>
        <CardContent className="p-0">
          <Table data-testid="my-schedule-table">
            <TableHeader><TableRow>
              <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('day')}>
                <div className="flex items-center gap-1">
                  Hari
                  {sortColumn === 'day' ? (
                    sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                  ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('jam')}>
                <div className="flex items-center gap-1">
                  Jam
                  {sortColumn === 'jam' ? (
                    sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                  ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('jtm')}>
                <div className="flex items-center gap-1">
                  JTM
                  {sortColumn === 'jtm' ? (
                    sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                  ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('class')}>
                <div className="flex items-center gap-1">
                  Kelas
                  {sortColumn === 'class' ? (
                    sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                  ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('subject')}>
                <div className="flex items-center gap-1">
                  Mata Pelajaran
                  {sortColumn === 'subject' ? (
                    sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                  ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                </div>
              </TableHead>
              <TableHead className="cursor-pointer hover:bg-slate-50" onClick={() => handleSort('room')}>
                <div className="flex items-center gap-1">
                  Ruang
                  {sortColumn === 'room' ? (
                    sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                  ) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                </div>
              </TableHead>
              {!isSiswa && <TableHead>Status</TableHead>}
              {!isSiswa && <TableHead className="text-right">Aksi</TableHead>}
            </TableRow></TableHeader>
            <TableBody>
              {sortedItems.map((s) => (
                <TableRow key={s.id} data-testid={`schedule-row-${s.id}`}>
                  <TableCell className="capitalize font-semibold">{DAY_LABELS[s.day]}</TableCell>
                  <TableCell className="font-mono text-sm">
                    <div>{s.hour_range || `Jam ke-${s.slot_index + 1 || '?'}`}</div>
                    <div className="text-[10px] text-slate-500">{s.time_range || `${s.start_time}–${s.end_time}`}</div>
                  </TableCell>
                  <TableCell className="font-semibold text-[#006837]">
                    {s.jtm_count || 1} JTM
                  </TableCell>
                  <TableCell>{s.class_name || '-'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono bg-slate-100 rounded px-1.5 py-0.5">{s.subject_code}</span>
                      <span className="text-sm">{s.subject_name || '-'}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{s.room_name || '-'}</TableCell>
                  {!isSiswa && <TableCell><StatusBadge status={s.status} /></TableCell>}
                  {!isSiswa && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {s.status === 'draft' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleSubmitDraft(s)}
                              className="border-blue-300 text-blue-700 hover:bg-blue-50 gap-1"
                              data-testid={`submit-${s.id}`}>
                              <Send className="h-3.5 w-3.5" /> Kirim
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-${s.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => handleDelete(s)} className="text-rose-600" data-testid={`delete-${s.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {s.status === 'submitted' && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <Send className="h-3 w-3 text-blue-600" /> Menunggu Persetujuan
                          </Badge>
                        )}
                        {s.status === 'approved' && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Disetujui
                          </Badge>
                        )}
                        {s.status === 'locked' && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <Lock className="h-3 w-3 text-rose-600" /> Final
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-500">
                  <Calendar className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                  <div className="font-semibold">Belum ada jadwal</div>
                  <div className="text-sm mt-1">Klik "Tambah Jadwal" untuk mulai mengatur jadwal mengajar Anda</div>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Jadwal Draft' : 'Tambah Jadwal Baru'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <Label>Hari</Label>
              <Select value={form.day} onValueChange={(v) => setForm({ ...form, day: v })}>
                <SelectTrigger data-testid="form-day"><SelectValue /></SelectTrigger>
                <SelectContent>{ALL_DAYS.map((d) => <SelectItem key={d} value={d}>{DAY_LABELS[d]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {/* v1.1.1: Multi-Slot Selector */}
            <div className="col-span-2">
              <Label>Jam Mengajar * (Klik untuk pilih lebih dari 1 jam)</Label>
              <div className="grid grid-cols-3 gap-2 mt-2 max-h-64 overflow-y-auto p-2 border border-slate-200 rounded">
                {teachingSlots.map((slot, index) => {
                  const isSelected = selectedSlots.includes(slot.originalIndex);
                  const isBreak = slot.is_break;

                  return (
                    <button
                      key={index}
                      type="button"
                      className={`p-3 rounded border text-left transition-all ${
                        isBreak
                          ? 'bg-slate-100 border-slate-300 cursor-not-allowed opacity-60'
                          : isSelected
                          ? 'bg-[#006837] text-white border-[#006837] shadow-md'
                          : 'bg-white border-slate-300 hover:border-[#006837] hover:bg-[#006837]/5'
                      }`}
                      onClick={() => !isBreak && handleSlotToggle(index)}
                      disabled={isBreak}
                      data-testid={`slot-selector-${index}`}
                    >
                      <div className="font-semibold text-sm">
                        {isBreak ? '🕐 ' : ''}{slot.name || `Jam ke-${slot.originalIndex + 1}`}
                      </div>
                      <div className="text-xs mt-1 opacity-90">
                        {slot.start_time} - {slot.end_time}
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedSlots.length > 0 && (
                <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-sm">
                  <span className="font-semibold text-emerald-800">Terpilih:</span>{' '}
                  <span className="text-emerald-700">
                    Jam ke-{selectedSlots.map(i => i + 1).join(', ')}
                  </span>
                  {(() => {
                    const selectedTeachingSlots = teachingSlots.filter(s => selectedSlots.includes(s.originalIndex));
                    selectedTeachingSlots.sort((a, b) => a.originalIndex - b.originalIndex);
                    const firstSlot = selectedTeachingSlots[0];
                    const lastSlot = selectedTeachingSlots[selectedTeachingSlots.length - 1];

                    if (firstSlot && lastSlot) {
                      return (
                        <span className="text-emerald-600 ml-2">
                          ({firstSlot.start_time} - {lastSlot.end_time})
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label>Kelas *</Label>
              <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                <SelectTrigger data-testid="form-class"><SelectValue placeholder="Pilih kelas..." /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Mata Pelajaran *</Label>
              <Select value={form.subject_id} onValueChange={(v) => setForm({ ...form, subject_id: v })}>
                <SelectTrigger data-testid="form-subject"><SelectValue placeholder="Pilih mapel..." /></SelectTrigger>
                <SelectContent>{subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.code} - {s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Alert className="border-emerald-200 bg-emerald-50">
                <Info className="h-4 w-4 text-emerald-700" />
                <AlertDescription className="text-emerald-900 text-xs">
                  💡 <strong>Ruang akan otomatis mengikuti ruang kelas yang dipilih.</strong>
                </AlertDescription>
              </Alert>
            </div>
            <div className="col-span-2">
              <Alert className="border-amber-200 bg-amber-50">
                <AlertCircle className="h-4 w-4 text-amber-700" />
                <AlertDescription className="text-amber-900 text-xs">
                  Jadwal akan tersimpan sebagai <strong>Draft</strong>. Setelah selesai mengisi semua jadwal, klik <strong>Kirim ke Admin</strong> untuk submit.
                </AlertDescription>
              </Alert>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={handleSubmit} className="bg-[#006837] hover:bg-[#0B7A3B]" data-testid="form-submit">
              {editing ? 'Simpan Perubahan' : 'Tambah sebagai Draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className={`rounded-xl border p-4 ${color}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="text-2xl font-extrabold tabular-nums mt-1">{value}</div>
    </div>
  );
}
